/**
 * Service de persistance abstrait qui fonctionne sur mobile ET web
 * Utilise AsyncStorage sur mobile et localStorage/IndexedDB sur web
 */

import {Platform} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

// Interface pour l'abstraction de stockage
interface StorageAdapter {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
  getAllKeys(): Promise<string[]>;
  multiGet(keys: string[]): Promise<[string, string | null][]>;
  multiSet(items: [string, string][]): Promise<void>;
}

// Adapter pour React Native (mobile)
class AsyncStorageAdapter implements StorageAdapter {
  async getItem(key: string): Promise<string | null> {
    return AsyncStorage.getItem(key);
  }

  async setItem(key: string, value: string): Promise<void> {
    return AsyncStorage.setItem(key, value);
  }

  async removeItem(key: string): Promise<void> {
    return AsyncStorage.removeItem(key);
  }

  async getAllKeys(): Promise<string[]> {
    return AsyncStorage.getAllKeys();
  }

  async multiGet(keys: string[]): Promise<[string, string | null][]> {
    return AsyncStorage.multiGet(keys);
  }

  async multiSet(items: [string, string][]): Promise<void> {
    return AsyncStorage.multiSet(items);
  }
}

// Adapter pour Web (localStorage)
// Note: On utilise 'any' pour window car TypeScript ne connaît pas window.localStorage par défaut
declare const window: any;

class LocalStorageAdapter implements StorageAdapter {
  private getStorage(): Storage | null {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }
    return window.localStorage;
  }

  async getItem(key: string): Promise<string | null> {
    const storage = this.getStorage();
    if (!storage) return null;
    return storage.getItem(key);
  }

  async setItem(key: string, value: string): Promise<void> {
    const storage = this.getStorage();
    if (!storage) {
      throw new Error('localStorage is not available');
    }
    storage.setItem(key, value);
  }

  async removeItem(key: string): Promise<void> {
    const storage = this.getStorage();
    if (!storage) return;
    storage.removeItem(key);
  }

  async getAllKeys(): Promise<string[]> {
    const storage = this.getStorage();
    if (!storage) return [];
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key) keys.push(key);
    }
    return keys;
  }

  async multiGet(keys: string[]): Promise<[string, string | null][]> {
    const storage = this.getStorage();
    if (!storage) {
      return keys.map(key => [key, null]);
    }
    return keys.map(key => [key, storage.getItem(key)]);
  }

  async multiSet(items: [string, string][]): Promise<void> {
    const storage = this.getStorage();
    if (!storage) {
      throw new Error('localStorage is not available');
    }
    items.forEach(([key, value]) => {
      storage.setItem(key, value);
    });
  }
}

// Détecter la plateforme et utiliser le bon adapter
const getStorageAdapter = (): StorageAdapter => {
  // Sur web, utiliser localStorage
  // Platform.OS peut être 'web' en React Native Web
  if (Platform.OS === 'web' || (typeof window !== 'undefined' && window.localStorage)) {
    return new LocalStorageAdapter();
  }
  // Sur mobile, utiliser AsyncStorage
  return new AsyncStorageAdapter();
};

class StorageService {
  private adapter: StorageAdapter;
  private initialized = false;

  constructor() {
    this.adapter = getStorageAdapter();
  }

  async init(): Promise<void> {
    if (this.initialized) {
      return;
    }
    // Re-détecter la plateforme au cas où on change de contexte
    this.adapter = getStorageAdapter();
    this.initialized = true;
  }

  async getItem(key: string): Promise<string | null> {
    return this.adapter.getItem(key);
  }

  async setItem(key: string, value: string): Promise<void> {
    return this.adapter.setItem(key, value);
  }

  async removeItem(key: string): Promise<void> {
    return this.adapter.removeItem(key);
  }

  async getAllKeys(): Promise<string[]> {
    return this.adapter.getAllKeys();
  }

  async multiGet(keys: string[]): Promise<[string, string | null][]> {
    return this.adapter.multiGet(keys);
  }

  async multiSet(items: [string, string][]): Promise<void> {
    return this.adapter.multiSet(items);
  }
}

export default new StorageService();
